import { buildSlope } from '../slopes/build'
import { slopeById } from '../slopes/roster'

/**
 * Building a slope into the transferable payload the page runs on.
 *
 * There is no par any more. The game is arcade best-times: the only clock that
 * matters is yours, and the only ghost is your own best run. Scrapping the
 * beam-search par solve also cut the slowest stage out of every slope load,
 * and removed the one system that kept vetoing forest density — the par line
 * always needed a corridor kept clear enough for a solver to thread at speed.
 */

export interface SlopePayload {
  minX: number; minZ: number; width: number; length: number; cell: number
  height: Float32Array
  snowWeights: Float32Array
  rock: Float32Array
  concavity: Float32Array
  exposure: Float32Array
  trap: Float32Array
  sluffProne: Float32Array
  obstacles: Array<{
    x: number; z: number; y: number; radius: number; height: number
    kind: 'tree' | 'rock'; severity: number; variant: number
  }>
  slopeId: string
  slopeVersion: number
  name: string
  blurb: string
  difficulty: string
  conditions: string
  vertical: number
  start: { x: number; z: number }
  checkpointZ: number[]
  finishZ: number
  sinks: number
  buildMs: number
}

export function buildPayload(
  slopeId: string,
  onProgress?: (stage: string) => void,
): SlopePayload {
  const def = slopeById(slopeId)

  onProgress?.('shaping the slope')
  const built = buildSlope(def)

  const hf = built.hf
  return {
    minX: hf.minX, minZ: hf.minZ, width: hf.width, length: hf.length, cell: hf.cell,
    height: hf.height,
    snowWeights: hf.snowWeights,
    rock: hf.rock,
    concavity: hf.concavity,
    exposure: hf.exposure,
    trap: hf.trap,
    sluffProne: hf.sluffProne,
    obstacles: built.obstacles.items.map((o) => ({ ...o })),
    slopeId: def.id,
    slopeVersion: def.version,
    name: def.name,
    blurb: def.blurb,
    difficulty: def.difficulty,
    conditions: def.conditions.summary,
    vertical: def.vertical,
    start: built.start,
    checkpointZ: built.checkpointZ,
    finishZ: built.finishZ,
    sinks: built.sinks,
    buildMs: built.buildMs,
  }
}

export function payloadTransfers(p: SlopePayload): ArrayBufferLike[] {
  const t: ArrayBufferLike[] = [
    p.height.buffer, p.snowWeights.buffer, p.rock.buffer,
    p.concavity.buffer, p.exposure.buffer, p.trap.buffer, p.sluffProne.buffer,
  ]
  return t
}

